'use client';

import { c as _c } from "react/compiler-runtime";
import type React from 'react';
import type { Theme } from 'src/Theme';
import Button from '@mui/material/Button';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { InputHelperText } from 'src/components/Atoms/InputHelperText';
import { FlexColumn } from 'src/components/Atoms/FlexBox';
import { inspect, rejectionReason } from '../lib/dev-credentials';

/**
 * Where a platform token gets pasted, until the platform can mint one the chat
 * backend accepts.
 *
 * Not a URL parameter, deliberately. A JWT in a query string ends up in browser
 * history, in the referrer of anything the page links to, and in every access
 * log between here and there. A paste box into local storage keeps it out of all
 * three.
 */

const Panel = styled(FlexColumn)(({
  theme
}: {
  theme: Theme;
}) => ({
  position: 'fixed',
  bottom: theme.sizes.spacing['08'],
  right: '5rem',
  zIndex: 9998,
  width: 'min(420px, calc(100vw - 2.5rem))',
  gap: theme.sizes.spacing['05'],
  padding: theme.sizes.spacing['07'],
  borderRadius: theme.sizes.radius.xl,
  border: `${theme.sizes.borderWidth[1]} solid ${theme.colors.border.secondary}`,
  backgroundColor: theme.colors.surface.primary,
  boxShadow: theme.boxShadows[6]
}));

/**
 * A styled textarea rather than `TextInput`: the shared `InputBase` fixes its
 * root to one line and clamps the input with `-webkit-line-clamp: 1`, so
 * `multiline` overflows it. This follows the composer's `TextareaAutosize`
 * pattern instead.
 *
 * Monospaced deliberately, and with a literal because the theme has only the
 * one family: a token is easier to eyeball for a truncated paste in a fixed
 * pitch. Dev-only, and goes away with the paste flow.
 */
const TokenInput = styled(TextareaAutosize)(({
  theme
}: {
  theme: Theme;
}) => ({
  width: '100%',
  resize: 'none',
  boxSizing: 'border-box',
  padding: theme.sizes.spacing['05'],
  borderRadius: theme.sizes.radius.sm,
  border: `${theme.sizes.borderWidth[1]} solid ${theme.colors.border.secondary}`,
  backgroundColor: theme.colors.surface.primary,
  color: theme.colors.text.primary,
  fontFamily: 'monospace',
  fontSize: theme.fonts.size.xs,
  lineHeight: theme.fonts.lineHeight['leading-5'],
  outline: 'none',
  '&::placeholder': {
    color: theme.colors.text.tertiary
  },
  '&:focus': {
    borderColor: theme.colors.brand.blaze[500]
  }
}));
export interface TokenPromptProps {
  apiUrl: string;
  onToken: (token: string) => void;
}
const TokenPrompt: React.FC<TokenPromptProps> = t0 => {
  const $ = _c(34);
  const {
    apiUrl,
    onToken
  } = t0;
  const [value, setValue] = useState("");
  const [error, setError] = useState();
  let t1;
  if ($[0] !== onToken || $[1] !== value) {
    t1 = () => {
      const token = value.trim();
      if (!token) {
        return;
      }
      const reason = rejectionReason(token);
      if (reason) {
        setError(reason);
        return;
      }
      onToken(token);
    };
    $[0] = onToken;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const submit = t1;
  let t2;
  if ($[3] !== error || $[4] !== value) {
    t2 = value.trim() && !error ? inspect(value.trim()) : undefined;
    $[3] = error;
    $[4] = value;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const info = t2;
  const firstTeam = info?.teams[0];
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Typography variant="body-xs" sx={_temp}>Ask me anything</Typography>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <FlexColumn>{t3}<Typography variant="body-sm" sx={{
        fontWeight: 600
      }}>Paste a platform token</Typography></FlexColumn>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = `The chat is pointed at ${apiUrl}, which does not yet accept this app's own session token. `;
  let t6;
  if ($[8] !== t5) {
    t6 = <Typography variant="body-xs" sx={_temp2}>{t5}Paste one from the platform and it will be kept in this browser.</Typography>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = event => {
      setValue(event.target.value);
      setError(undefined);
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== submit) {
    t8 = event_0 => {
      if (event_0.key === "Enter" && (event_0.metaKey || event_0.ctrlKey)) {
        submit();
      }
    };
    $[11] = submit;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t8 || $[14] !== value) {
    t9 = <TokenInput minRows={4} maxRows={8} spellCheck={false} placeholder={"eyJhbGciOi\u2026"} aria-label="Platform token" value={value} onChange={t7} onKeyDown={t8} />;
    $[13] = t8;
    $[14] = value;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== error) {
    t10 = error && <InputHelperText error={true}>{error}</InputHelperText>;
    $[16] = error;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t9) {
    t11 = <FlexColumn>{t9}{t10}</FlexColumn>;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== firstTeam || $[22] !== info) {
    t12 = info && firstTeam && <Typography variant="body-xs" sx={_temp3}>{`${info.displayName ? `${info.displayName} · ` : ""}${info.teams.length === 1 ? firstTeam : `${info.teams.length} teams`}`}</Typography>;
    $[21] = firstTeam;
    $[22] = info;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== value) {
    t13 = value.trim();
    $[24] = value;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const t14 = !t13;
  let t15;
  if ($[26] !== submit || $[27] !== t14) {
    t15 = <Button variant="contained" onClick={submit} disabled={t14}>Use this token</Button>;
    $[26] = submit;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t11 || $[30] !== t12 || $[31] !== t15 || $[32] !== t6) {
    t16 = <Panel>{t4}{t6}{t11}{t12}{t15}</Panel>;
    $[29] = t11;
    $[30] = t12;
    $[31] = t15;
    $[32] = t6;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  return t16;
};
export default TokenPrompt;
function _temp(theme) {
  return {
    color: theme.colors.brand.blaze[700],
    textTransform: "uppercase",
    letterSpacing: theme.fonts.letterSpacing.wide,
    fontWeight: 600
  };
}
function _temp2(theme_0) {
  return {
    color: theme_0.colors.text.tertiary
  };
}
function _temp3(theme_1) {
  return {
    color: theme_1.colors.text.tertiary
  };
}